export const MEDIA_IMAGE_REGEX = /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i;
export const MEDIA_AUDIO_REGEX = /\.(mp3|wav|ogg|m4a|aac|flac)$/i;
export const MEDIA_VIDEO_REGEX = /\.(mp4|mov|webm|mkv|avi|m4v)$/i;

export const normalizeUrls = (raw: string): string[] =>
  raw
    .split(/\r?\n|,/)
    .map(url => url.trim())
    .filter(Boolean);

export type MediaType =
  | 'image'
  | 'audio'
  | 'video'
  | 'youtube'
  | 'vimeo'
  | 'loom'
  | 'pdf'
  | 'link';

export const getMediaType = (url: string): MediaType => {
  const lower = url.toLowerCase();
  if (lower.includes('youtube.com') || lower.includes('youtu.be'))
    return 'youtube';
  if (lower.includes('vimeo.com')) return 'vimeo';
  if (lower.includes('loom.com')) return 'loom';
  if (MEDIA_IMAGE_REGEX.test(lower)) return 'image';
  if (
    MEDIA_AUDIO_REGEX.test(lower) ||
    lower.includes('spotify.com') ||
    lower.includes('apple.com')
  )
    return 'audio';
  if (MEDIA_VIDEO_REGEX.test(lower)) return 'video';
  if (lower.endsWith('.pdf')) return 'pdf';
  return 'link';
};

export const getEmbedUrl = (url: string, type: MediaType): string => {
  if (type === 'youtube') {
    if (url.includes('watch?v=')) {
      return url.replace('watch?v=', 'embed/');
    }
    if (url.includes('youtu.be/')) {
      const id = url.split('youtu.be/')[1];
      return `https://www.youtube.com/embed/${id}`;
    }
  }
  if (type === 'vimeo') {
    const id = url.split('/').pop();
    return id ? `https://player.vimeo.com/video/${id}` : url;
  }
  if (type === 'loom') {
    return url.replace('/share/', '/embed/');
  }
  return url;
};

export const isValidMediaFile = (file: File): boolean => {
  return (
    file.type.startsWith('image/') ||
    file.type.startsWith('video/') ||
    file.type === 'application/pdf' ||
    file.type.startsWith('audio/')
  );
};

export const getSpotifyEmbedUrl = (url: string): string | null => {
  if (!url.includes('spotify.com')) {
    return null;
  }

  if (url.includes('/track/')) {
    const trackId = url.split('/track/')[1]?.split('?')[0];
    return trackId ? `https://open.spotify.com/embed/track/${trackId}` : null;
  }

  if (url.includes('/album/')) {
    const albumId = url.split('/album/')[1]?.split('?')[0];
    return albumId ? `https://open.spotify.com/embed/album/${albumId}` : null;
  }

  if (url.includes('/playlist/')) {
    const playlistId = url.split('/playlist/')[1]?.split('?')[0];
    return playlistId
      ? `https://open.spotify.com/embed/playlist/${playlistId}`
      : null;
  }

  return null;
};

export const isAppleMusicUrl = (url: string): boolean => {
  return url.includes('music.apple.com');
};

export const isUrl = (url: string): boolean => {
  try {
    new URL(url.trim());
    return true;
  } catch {
    return false;
  }
};
